import { ChevronLeft, ChevronRight, ExpandLess, ExpandMore, Logout } from '@mui/icons-material'
import {
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { useNavigate, useLocation } from 'react-router'
import { RolType, SidebarItems } from './SidebarItems'
import { connect } from 'react-redux'
import { themePalette } from '../themePalette'
import Loading from '../../Common/Loading'
import UserInfo from './UserInfo'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { OfrecerPremiumContext } from '../../../context/OfrecerPremium/ofrecerPremium.context'
import useLocal from '../../../pages/Products/hooks/locals.hook'
import DropdownList from './subItems'
import { useAnalytics } from '../../../hooks/useAnalytics'

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end'
}))

const Sidebar = (props: any) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { onOpen } = useContext(OfrecerPremiumContext)
  const location = useLocation()
  const { handleDrawerClose, open, rol, setIsLoggingOut, logged } = props
  const { userLogged: user } = logged
  const { local, loading, showIntegraciones } = useLocal(user.codLocal)
  const isUser: boolean = rol === RolType.USER
  const [openRubros, setOpenRubros] = useState<boolean>(false)
  const [openLocales, setOpenLocales] = useState<boolean>(false)
  const [openProductos, setOpenProductos] = useState<boolean>(false)
  const [openContratos, setOpenContratos] = useState<boolean>(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const drawerWidth = isMobile ? 250 : 290
  const [showBotonPremium, setShowBotonPremium] = useState<boolean>(false)
  const version = process.env.REACT_APP_VERSION
  const { track } = useAnalytics()
  const isAdmin = rol === RolType.ADMIN

  const handleCloseSidebar = useCallback(() => {
    if (isMobile) handleDrawerClose()
  }, [isMobile, handleDrawerClose])

  useEffect(() => {
    const show = local?.config?.find((c: any) => c.name === 'premium_ofrecerPremium')?.value
    setShowBotonPremium(show === 'true' ? true : false)
  }, [local])

  const handleOfrecerPremium = () => {
    track('vendor_premium_popup_abrir', {
      origin: 'sidebar'
    })
    onOpen()
  }

  if (loading) return <Loading />

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          bgcolor: themePalette.bgcolorsidebar,
          color: themePalette.colorsidebar
        }
      }}
      variant={isMobile ? 'temporary' : 'persistent'}
      onClose={handleCloseSidebar}
      anchor="left"
      open={open}
    >
      <DrawerHeader>
        <IconButton
          sx={{
            color: themePalette.colorsidebar,
            bgcolor: themePalette.defaultHoverSidebar,
            ':hover': { bgcolor: themePalette.alternativeHover }
          }}
          onClick={handleDrawerClose}
        >
          {theme.direction === 'ltr' ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </DrawerHeader>
      {/* User Info */}
      {local && isUser && <UserInfo local={local} user={user} />}
      <Divider sx={{ bgcolor: themePalette.defaultHoverSidebar }} />
      {/* Links */}

      {local && showBotonPremium && isUser && (
        <ListItem
          disablePadding
          onClick={handleOfrecerPremium}
          sx={{
            background: 'linear-gradient(90deg, #ffdc40 0%, #caa70b 100%)',
            ':hover': {
              background: 'linear-gradient(90deg, #ffdc40 0%, #e2c64a 100%)'
            },
            color: '#000000'
            // ":hover > div > div > svg": {
            //   color: "#fffab2",
            // },
          }}
        >
          <ListItemButton>
            {/* <ListItemIcon>
              <Star />
            </ListItemIcon> */}
            <ListItemText
              primary={local.config.find((c: any) => c.name === 'premium_sidebarBotonUpgradeText')?.value}
            />
          </ListItemButton>
        </ListItem>
      )}
      <List>
        {SidebarItems.map((item, i) => {
          if (!item.rol.includes(rol)) return null
          if (i === 0 && isMobile) return null
          if (item.title === 'Integraciones' && !showIntegraciones) return null

          const openMap: Record<string, boolean> = {
            Rubros: openRubros,
            Locales: openLocales,
            Productos: openProductos,
            Contratos: openContratos
          }

          const toggleMap: Record<string, () => void> = {
            Rubros: () => setOpenRubros((v) => !v),
            Locales: () => setOpenLocales((v) => !v),
            Productos: () => setOpenProductos((v) => !v),
            Contratos: () => setOpenContratos((v) => !v)
          }

          const hasSubItems = !!toggleMap[item.title]
          const isOpen = openMap[item.title]

          return (
            <React.Fragment key={`${item.title}-${i}`}>
              <ListItem
                disablePadding
                sx={{
                  bgcolor: location.pathname === item.path ? '#dddddd' : 'transparent',
                  ':hover': { bgcolor: themePalette.defaultHoverSidebar }
                }}
              >
                <ListItemButton
                  onClick={() => {
                    item.title === 'Contratos' && !isAdmin ? toggleMap[item.title]?.() : navigate(item.path)
                    handleCloseSidebar()
                  }}
                >
                  <ListItemIcon sx={{ color: themePalette.colorsidebar }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.title} />
                </ListItemButton>

                {hasSubItems && (
                  <IconButton
                    size="small"
                    sx={{ color: themePalette.colorsidebar, mr: 1 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleMap[item.title]()
                    }}
                  >
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                )}
              </ListItem>

              {/* Collapses igual que antes */}
              {item.title === 'Rubros' && (
                <Collapse in={openRubros} timeout="auto" unmountOnExit>
                  <DropdownList
                    title={item.title}
                    items={item?.subItems}
                    navigate={navigate}
                    location={location}
                    rol={rol}
                  />
                </Collapse>
              )}
              {item.title === 'Locales' && (
                <Collapse in={openLocales} timeout="auto" unmountOnExit>
                  <DropdownList
                    title={item.title}
                    items={item?.subItems}
                    navigate={navigate}
                    location={location}
                    rol={rol}
                  />
                </Collapse>
              )}
              {item.title === 'Productos' && (
                <Collapse in={openProductos} timeout="auto" unmountOnExit>
                  <DropdownList
                    title={item.title}
                    items={item?.subItems}
                    navigate={navigate}
                    location={location}
                    rol={rol}
                  />
                </Collapse>
              )}
              {item.title === 'Contratos' && (
                <Collapse in={openContratos} timeout="auto" unmountOnExit>
                  <DropdownList
                    title={item.title}
                    items={item?.subItems}
                    navigate={navigate}
                    location={location}
                    rol={rol}
                  />
                </Collapse>
              )}
            </React.Fragment>
          )
        })}

        <ListItem
          onClick={() => setIsLoggingOut(true)}
          disablePadding
          sx={{ ':hover': { bgcolor: themePalette.defaultHoverSidebar } }}
        >
          <ListItemButton>
            <ListItemIcon sx={{ color: themePalette.logout }}>
              <Logout />
            </ListItemIcon>
            <ListItemText sx={{ color: themePalette.logout }} primary={'Cerrar Sesión'} />
          </ListItemButton>
        </ListItem>
      </List>
      {version && (
        <div
          style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: '#999',
            padding: '8px',
            marginTop: 'auto'
          }}
        >
          Versión {version}
        </div>
      )}
    </Drawer>
  )
}

const mapStateToProps = (state: any) => {
  return {
    logged: state.logged
  }
}

export default connect(mapStateToProps)(Sidebar)
